import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/LanguageSelector';

export default function VerificationPendingPage() {
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const logout = useAuthStore((state) => state.logout);
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const handleResend = async () => {
    setIsSending(true);
    try {
      await apiClient.resendVerification();
      setSent(true);
      addToast(t('auth.verification.resendSuccess'), 'success');
    } catch (err: any) {
      addToast(err?.response?.data?.message || t('auth.verification.sendError'), 'error');
    } finally {
      setIsSending(false);
    }
  };

  const handleRefresh = async () => {
    setIsChecking(true);
    try {
      const profile = await apiClient.getProfile();
      setUser(profile);
      if (profile.emailVerified) {
        addToast(t('auth.verification.verifiedSuccess'), 'success');
        navigate('/pricing', { replace: true });
      } else {
        addToast(t('auth.verification.notYetVerified'), 'info');
      }
    } catch {
      addToast(t('auth.verification.refreshError'), 'error');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-navy via-primary-800 to-primary-600 flex items-center justify-center px-4">
      <div className="absolute top-4 right-4 z-20">
        <LanguageSelector />
      </div>
      <div className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8 text-center">
        <div className="w-20 h-20 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-6">
          <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-neutral-900 mb-3">{t('auth.verification.title')}</h1>
        <p className="text-neutral-600 mb-2">{t('auth.verification.sent')}</p>
        <p className="font-bold text-neutral-900 mb-6">{user?.email}</p>
        <p className="text-sm text-neutral-500 mb-8">{t('auth.verification.spam')}</p>

        <div className="space-y-3">
          <button
            onClick={handleResend}
            disabled={isSending || sent}
            className="w-full py-3 rounded-lg bg-primary-600 text-white font-bold hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {sent ? t('auth.verification.resent') : isSending ? t('auth.verification.sending') : t('auth.verification.resend')}
          </button>

          <button
            onClick={handleRefresh}
            disabled={isChecking}
            className="w-full py-3 rounded-lg border border-neutral-200 text-neutral-700 font-medium hover:bg-neutral-50 transition-colors disabled:opacity-50"
          >
            {isChecking ? t('auth.verification.refreshing') : t('auth.verification.refresh')}
          </button>

          <button
            onClick={() => { logout(); window.location.href = '/login'; }}
            className="w-full py-2 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            {t('auth.verification.logout')}
          </button>
        </div>
      </div>
    </div>
  );
}
